import { useEffect, useState } from 'react'
import { fetchRoster, type RosterPlayer } from '@/lib/npbApi'
import type { Player } from '@/domain/player'

export interface ApiError {
  readonly message: string
  readonly reason: string
}

interface UseRosterResult {
  readonly players: Player[]
  readonly loading: boolean
  readonly error: ApiError | null
}

function toPlayer(rosterPlayer: RosterPlayer): Player {
  return {
    id: rosterPlayer.playerId,
    name: rosterPlayer.name,
    nameKana: rosterPlayer.nameKana,
    team: rosterPlayer.team,
    position: rosterPlayer.position,
    number: rosterPlayer.uniformNumber,
  }
}

/**
 * アプリ起動時にロースター（全球団統合の選手一覧）を1回取得するフック（Issue #9「決めること」:
 * アプリ起動時にロースター全件を一度取得し、以降は検索・選択に使い回す設計）。
 * 取得中はローディング、失敗時はエラー内容を返す（画面は壊さない。呼び出し側が表示を決める）。
 */
export function useRoster(): UseRosterResult {
  const [players, setPlayers] = useState<Player[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ApiError | null>(null)

  useEffect(() => {
    let cancelled = false

    setLoading(true)
    setError(null)

    fetchRoster()
      .then((result) => {
        if (cancelled) return
        if (result.ok) {
          setPlayers(result.data.map(toPlayer))
          setError(null)
        } else {
          setPlayers([])
          setError({ message: result.error, reason: result.reason })
        }
        setLoading(false)
      })
      .catch(() => {
        // fetchRoster 自体は例外を投げない設計だが、念のための保険（画面を壊さない）
        if (cancelled) return
        setPlayers([])
        setError({ message: '選手一覧の取得に失敗しました。', reason: 'unexpected_error' })
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return { players, loading, error }
}
